import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import { addWeeks, startOfDay } from 'date-fns';

import {
  schedulingV4BookSelfScheduledAppointmentMutation,
  schedulingV4ScheduleOrRescheduleAppointmentMutation,
  schedulingV4VerifyPatientBookingMutation,
} from '@/api/generated/@tanstack/react-query.gen';
import {
  AvailabilityCalendar,
  type AvailabilityCalendarFormData,
} from '@/components/AvailabilityCalendar';
import { ErrorModal } from '@/components/ErrorModal';
import { NavigationBar } from '@/components/NavigationBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DEFAULT_TELEHEALTH_CONTACT_TYPE,
  NEW_PATIENT_VISIT_DURATION_IN_MINUTES,
  TELEHEALTH_CONTACT_TYPE,
  type TelehealthContactType,
} from '@/lib/constants';
import { formatDobForApi } from '@/lib/formatters';
import { logger } from '@/lib/logger';
import type { Appointment } from '@/types/order.type';

import { SelfScheduleConfirmation } from './components/SelfScheduleConfirmation';
import { SelfScheduleVerifyDobStep } from './components/SelfScheduleVerifyDobStep';
import { resolveSelfScheduleCalendarTypes } from './resolve-self-schedule-calendar-types';

const SELF_SCHEDULE_CALENDAR_DEFAULTS = {
  defaultAppointmentTypeId: import.meta.env.VITE_HEALTHIE_APPOINTMENT_TYPE_NEW_PATIENT_VISIT_ID,
  defaultContactType: DEFAULT_TELEHEALTH_CONTACT_TYPE,
  additionalAppointmentTypeId: import.meta.env.VITE_HEALTHIE_APPOINTMENT_TYPE_VANTAGE_VISIT_ID,
  additionalContactType: TELEHEALTH_CONTACT_TYPE.PHONE,
};

interface VerifiedContext {
  dob: string;
  patientState: string | null;
}

const SelfScheduleStep = {
  VERIFY_DOB: 'verify_dob',
  SELECT_SLOT: 'select_slot',
  CONFIRMATION: 'confirmation',
} as const;

type SelfScheduleStepValue = (typeof SelfScheduleStep)[keyof typeof SelfScheduleStep];

const STEPS: SelfScheduleStepValue[] = [
  SelfScheduleStep.VERIFY_DOB,
  SelfScheduleStep.SELECT_SLOT,
  SelfScheduleStep.CONFIRMATION,
];

/** `legacy` = POST /schedule; `unified` = POST /schedule/{order_public_id} */
type BookingApi = 'legacy' | 'unified';

interface SelfScheduleAppointmentProps {
  orderPublicId: string;
  bookingApi: BookingApi;
  /** When set (V2 search param), overrides hardcoded dual appointment-type defaults. */
  appointmentTypeId?: string;
  /** When set (V2 search param), overrides contact type (wins over appointment-type map). */
  contactType?: TelehealthContactType;
}

export const SelfScheduleAppointment = ({
  orderPublicId,
  bookingApi,
  appointmentTypeId,
  contactType,
}: SelfScheduleAppointmentProps) => {
  const calendarTypes = resolveSelfScheduleCalendarTypes(
    { appointmentTypeId, contactType },
    SELF_SCHEDULE_CALENDAR_DEFAULTS
  );

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = STEPS[currentStepIndex];

  const [verifiedContext, setVerifiedContext] = useState<VerifiedContext | null>(null);
  const [appointmentDetails, setAppointmentDetails] = useState<Appointment | null>(null);
  const [_patientPhoneNumber, setPatientPhoneNumber] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const verifyMutation = useMutation({
    ...schedulingV4VerifyPatientBookingMutation(),
    retry: false,
  });
  const legacyBookMutation = useMutation({
    ...schedulingV4BookSelfScheduledAppointmentMutation(),
    retry: false,
  });
  const unifiedBookMutation = useMutation({
    ...schedulingV4ScheduleOrRescheduleAppointmentMutation(),
    retry: false,
  });

  const appointmentDuration = NEW_PATIENT_VISIT_DURATION_IN_MINUTES;

  const startDate = startOfDay(new Date());
  const endDate = startOfDay(addWeeks(startDate, 2));

  const handleVerifyDob = async (dob: string) => {
    try {
      const result = await verifyMutation.mutateAsync({
        body: {
          order_public_id: orderPublicId,
          patient_dob: formatDobForApi(dob),
        },
      });
      setVerifiedContext({ dob, patientState: result.payload?.patient_state ?? null });
      setCurrentStepIndex(1);
    } catch {
      // Error state handled by verifyMutation.isError in the UI
    }
  };

  const handleSlotSubmit = async (data: AvailabilityCalendarFormData) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      if (
        !data.selectedSlot ||
        !data.selectedProviderId ||
        !data.selectedContactType ||
        !data.selectedAppointmentTypeId
      ) {
        throw new Error(
          'Selected slot, provider ID, contact type, or appointment type ID is missing'
        );
      }

      const patientDob = formatDobForApi(verifiedContext!.dob);

      const result =
        bookingApi === 'unified'
          ? await unifiedBookMutation.mutateAsync({
              path: { order_public_id: orderPublicId },
              body: {
                patient_dob: patientDob,
                appointment_date: data.selectedSlot,
                provider_id: data.selectedProviderId,
                timezone,
                appointment_type_id: data.selectedAppointmentTypeId,
                contact_type: data.selectedContactType,
              },
            })
          : await legacyBookMutation.mutateAsync({
              body: {
                order_public_id: orderPublicId,
                patient_dob: patientDob,
                appointment_date: data.selectedSlot,
                provider_id: data.selectedProviderId,
                timezone,
                appointment_type_id: data.selectedAppointmentTypeId,
                telehealth_contact_type: data.selectedContactType,
              },
            });

      setAppointmentDetails({
        scheduledTime: result.payload.date ? new Date(result.payload.date) : undefined,
        timeZone: timezone,
        durationInMinutes: appointmentDuration,
        provider: { name: null, phoneNumber: '' },
        contactType: data.selectedContactType,
      });
      setPatientPhoneNumber(result.payload.phone_number ?? '');
      setCurrentStepIndex(2);
    } catch (err) {
      logger.error('Failed to book appointment', { error: err, data, orderPublicId, bookingApi });
      setIsErrorModalOpen(true);
    }
  };

  return (
    <>
      {currentStep === SelfScheduleStep.VERIFY_DOB && (
        <SelfScheduleVerifyDobStep
          onSubmit={handleVerifyDob}
          isPending={verifyMutation.isPending}
          error={
            verifyMutation.isError
              ? 'We could not verify your identity. Please check your date of birth and try again.'
              : undefined
          }
        />
      )}

      {currentStep === SelfScheduleStep.SELECT_SLOT && verifiedContext && (
        <>
          <NavigationBar onBack={() => setCurrentStepIndex(0)} backLabel="Back" />
          <Card className="gap-6 sm:gap-6">
            <CardHeader>
              <CardTitle>
                Schedule a {appointmentDuration} min clinician {calendarTypes.visitKind}
              </CardTitle>
              <CardDescription>
                <p className="font-medium text-orchid-600">Important note</p>
                <p>Patient must be present for Telehealth call</p>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AvailabilityCalendar
                startDate={startDate}
                endDate={endDate}
                licensedInState={verifiedContext.patientState}
                onSubmit={handleSlotSubmit}
                appointmentTypeId={calendarTypes.appointmentTypeId}
                contactType={calendarTypes.contactType}
                additionalAppointmentTypeId={calendarTypes.additionalAppointmentTypeId}
                additionalContactType={calendarTypes.additionalContactType}
              />
            </CardContent>
          </Card>
        </>
      )}

      {currentStep === SelfScheduleStep.CONFIRMATION && appointmentDetails && (
        <SelfScheduleConfirmation appointment={appointmentDetails} />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isOpen={isErrorModalOpen}
          setIsOpen={setIsErrorModalOpen}
          title="Something went wrong"
          description="We're having trouble booking your appointment. Please try again."
        />
      )}
    </>
  );
};

/** Existing links: `/appointment/schedule/:orderPublicId` → POST /api/v4/scheduling/schedule */
export const SelfScheduleAppointmentLegacyRoute = () => {
  const { orderPublicId } = useParams({
    from: '/appLayout/wizardLayout/appointment/schedule/$orderPublicId',
  });
  return <SelfScheduleAppointment orderPublicId={orderPublicId} bookingApi="legacy" />;
};

/** V2 links: `/appointment/v2/schedule/:orderPublicId?appointmentTypeId=&contactType=` → POST /api/v4/scheduling/schedule/:orderPublicId */
export const SelfScheduleAppointmentV2Route = () => {
  const { orderPublicId } = useParams({
    from: '/appLayout/wizardLayout/appointment/v2/schedule/$orderPublicId',
  });
  const { appointmentTypeId, contactType } = useSearch({
    from: '/appLayout/wizardLayout/appointment/v2/schedule/$orderPublicId',
  });
  return (
    <SelfScheduleAppointment
      orderPublicId={orderPublicId}
      bookingApi="unified"
      appointmentTypeId={appointmentTypeId?.toString()}
      contactType={contactType}
    />
  );
};
